<template>
  <div v-if="loading" class="d-flex justify-center h-[500px] flex items-center">
    <app-loader></app-loader>
  </div>
  <div v-else class="mt-10">
    <div
      v-if="allExpenses?.duration?.is_locked"
      class="bg-amber-50 rounded-lg p-3 flex items-center gap-1 w-max mb-4"
    >
      <svg
        class="flex-shrink-0 w-4 h-4"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="#B45309"
        viewBox="0 0 20 20"
      >
        <path
          d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
        />
      </svg>
      <p class="text-sm text-amber-700">
        {{ $t("expenses.monthLockedContactController") }}
      </p>
    </div>
    <overview-heading></overview-heading>
    <div class="mb-3 flex">
      <app-radio-button
        name="Cash"
        v-model="selectedOption"
        :value="selectedOption"
        :disabled="
          currentPath === '/edit-pm-local' && isEditPmLocal && !isEditButton
        "
      >
      </app-radio-button>
      <app-radio-button
        name="Bank"
        class="ml-10"
        v-model="selectedOption"
        :value="selectedOption"
        :disabled="
          currentPath === '/edit-pm-local' && isEditPmLocal && !isEditButton
        "
      >
      </app-radio-button>
    </div>
    <div class="flex items-center gap-5 mb-5">
      <app-single-select
        id="expense-project-year"
        class="w-2/12"
        :label="$t('pmLocals.year')"
        :labelPlaceholder="$t('cheques.selYear')"
        :model-value="expense_year"
        :options="yearOptions"
        :disabled="
          currentPath === '/edit-pm-local' && isEditPmLocal && !isEditButton
        "
        @update:model-value="handleInputChange($event, 'year')"
      ></app-single-select>

      <app-single-select
        id="expense-project-month"
        class="w-2/12"
        :label="$t('pmLocals.month')"
        :labelPlaceholder="$t('cheques.selMonth')"
        :options="monthOptions"
        :model-value="expense_month"
        :disabled="
          currentPath === '/edit-pm-local' && isEditPmLocal && !isEditButton
        "
        @update:model-value="handleInputChange($event, 'month')"
      ></app-single-select>
    </div>

    <app-accordion
      class="mb-5"
      :accordionInfo="overViewInfo"
      :showFooter="true"
    >
      <template #content>
        <expense-info></expense-info>
      </template>
      <template #footer>
        <expense-balance-overview></expense-balance-overview>
      </template>
    </app-accordion>

    <!-- 314 error accordion banner -->
    <div
      v-if="showErrorBanner && multipleTitles.length >= 1"
      class="bg-red-50 border border-red-200 rounded-lg my-4 overflow-hidden"
    >
      <div
        class="flex items-center justify-between p-3 cursor-pointer"
        @click="errorBannerOpen = !errorBannerOpen"
      >
        <div class="flex items-center gap-2">
          <svg
            class="flex-shrink-0 w-4 h-4"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="#B91C1C"
            viewBox="0 0 20 20"
          >
            <path
              d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
            />
          </svg>
          <p class="text-sm font-medium text-red-700">
            {{ $t("expenses.expenseEntryErrors") }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <v-icon
            :icon="errorBannerOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            size="18"
            class="text-red-500"
          ></v-icon>
          <v-icon
            icon="mdi-close"
            size="16"
            class="text-red-400 hover:text-red-600"
            @click.stop="showErrorBanner = false"
          ></v-icon>
        </div>
      </div>
      <div
        v-if="errorBannerOpen"
        class="border-t border-red-200 px-3 py-2 space-y-1"
      >
        <div
          v-for="(err, i) in multipleTitles"
          :key="i"
          class="text-sm text-red-700 flex items-start gap-1"
        >
          <span class="mt-0.5">•</span>
          <span>
            {{ err.message }}
            <strong>No. {{ err.number }}: {{ err.title }}</strong> —
            {{ err.key }} ({{ $t("expenses.availableAmount") }}:
            {{ err.amount }}, {{ $t("Payment Date".getTranslationKey()) }}:
            {{ err.pay_date }})
          </span>
        </div>
      </div>
    </div>

    <app-custom-table
      :headers="tableHeaders"
      :items="expense_data"
      :title="
        isEditExpense ? $t('expenses.editExpense') : $t('expenses.newExpense')
      "
      :show-menu="!shouldDisableInput"
      :dropdown-options="['Delete', 'View File']"
      :row-styles="rowErrorClass"
      @menu-click="handleMenuClick"
      table-name="expense-table"
      :fallback-message="{
        title: 'No expenses found',
        subtitle: 'Add a new expense to get started',
      }"
      @item-click="() => {}"
      class="mt-10"
      :show-currency="false"
    >
      <template #action-btn>
        <app-secondary-button
          v-if="allExpenses?.duration?.is_locked"
          :buttonLabel="$t('View Expense PDF'.getTranslationKey())"
          @click-event="navigateToExpensePdf"
        />
        <template v-else-if="!shouldDisableInput">
          <div class="flex items-center gap-2">
            <app-secondary-button
              v-if="
                isEditExpense && isAllBalanceInfoData?.can_lock_month !== false
              "
              :buttonLabel="$t('Lock Month'.getTranslationKey())"
              @click-event="openLockModal"
            >
              <template #icon><v-icon icon="mdi-lock-outline" /></template>
            </app-secondary-button>
            <app-primary-button
              :buttonLabel="$t('Add New Row'.getTranslationKey())"
              @click-event="addNewExpenseRow"
            >
              <template #icon><v-icon icon="mdi-plus" /></template>
            </app-primary-button>
          </div>
        </template>
      </template>

      <!-- Title Column -->
      <template #title="{ item }">
        <app-input-field
          :id="`title-${item.index}`"
          v-model="expense_data[item.index].title"
          placeholder="Title"
          :show-label="false"
          :disabled="shouldDisableInput || expense_data[item.index].status"
          height="40px"
          class="!w-[300px]"
          padding="6px"
        />
      </template>

      <!-- Document Date Column -->
      <template #document_date="{ item }">
        <app-date-picker
          :id="`document-date-${item.index}`"
          :max-date="
            expense_data[item.index].payment_date || lastDayOfSelectedMonth
          "
          :show-label="false"
          v-model="expense_data[item.index].document_date"
          :disabled="shouldDisableInput || expense_data[item.index].status"
        />
      </template>

      <!-- Payment Date Column -->
      <template #payment_date="{ item }">
        <app-date-picker
          :id="`payment-date-${item.index}`"
          :min-date="getPaymentMinDate(expense_data[item.index].document_date)"
          :max-date="lastDayOfSelectedMonth"
          :show-label="false"
          v-model="expense_data[item.index].payment_date"
          :disabled="shouldDisableInput || expense_data[item.index].status"
        />
      </template>

      <!-- Supplier Column -->
      <template #supplier="{ item }">
        <app-input-field
          :id="`supplier-${item.index}`"
          v-model="expense_data[item.index].supplier"
          placeholder="Supplier"
          :show-label="false"
          :disabled="shouldDisableInput || expense_data[item.index].status"
          height="40px"
          :input-styling="{ width: '110px' }"
          padding="6px"
        />
      </template>

      <!-- Currency Column -->
      <template #currency_id="{ item }">
        <app-single-select
          :id="`currency-${item.index}`"
          :label-placeholder="$t('cheques.selectPost')"
          :options="allCurrencies"
          v-model="expense_data[item.index].currency_id"
          :is-translation="false"
          :is-label="false"
          :disabled="shouldDisableInput || expense_data[item.index].status"
          :show-selected="true"
          container-height="40px"
        />
      </template>

      <!-- Amount Excl. Tax Column -->
      <template #total_amount_not_taxed="{ item }">
        <app-input-field
          :id="`amount-not-taxed-${item.index}`"
          v-model="expense_data[item.index].total_amount_not_taxed"
          type="number"
          placeholder="0"
          :show-label="false"
          :disabled="shouldDisableInput || expense_data[item.index].status"
          height="40px"
          :input-styling="{ width: '100px', textAlign: 'right' }"
          padding="6px"
          validation="floatingNumber"
        />
      </template>

      <!-- Amount Incl. Tax Column -->
      <template #total_amount_taxed="{ item }">
        <app-input-field
          :id="`amount-taxed-${item.index}`"
          v-model="expense_data[item.index].total_amount_taxed"
          type="number"
          placeholder="0"
          :show-label="false"
          :disabled="shouldDisableInput || expense_data[item.index].status"
          height="40px"
          :input-styling="{ width: '100px', textAlign: 'right' }"
          padding="6px"
          validation="floatingNumber"
        />
      </template>

      <!-- Budget Position Column -->
      <template #budget_id="{ item }">
        <app-single-select
          :id="`budget-position-${item.index}`"
          :label-placeholder="$t('cheques.selectPost')"
          :options="chequeBudgetPosition"
          v-model="expense_data[item.index].budget_id"
          :is-translation="false"
          :is-label="false"
          :disabled="shouldDisableInput || expense_data[item.index].status"
          :show-selected="true"
          container-height="40px"
        />
      </template>

      <!-- File Column -->
      <template #file_title="{ item }">
        <div v-if="!expense_data[item.index].file_title">
          <label
            :for="`fileInput-${item.index}`"
            :class="{
              'cursor-pointer':
                !expense_data[item.index].status && !shouldDisableInput,
            }"
          >
            <span class="text-blue-500">{{ $t("expenses.uploadFile") }}</span>
          </label>
          <input
            :disabled="shouldDisableInput || expense_data[item.index].status"
            :id="`fileInput-${item.index}`"
            type="file"
            @change="handleFileUpload($event, item.index)"
            style="display: none"
          />
        </div>
        <div
          v-else
          @click="viewfile(item.index)"
          class="cursor-pointer hover:underline"
        >
          {{
            expense_data[item.index].file_title.length > 10
              ? expense_data[item.index].file_title?.substring(0, 10) + "..."
              : expense_data[item.index].file_title
          }}
        </div>
      </template>

      <!-- Audit Status Column -->
      <template #status="{ item }">
        <div
          :class="{ 'cursor-pointer': !shouldDisableInput }"
          @click="
            shouldDisableInput
              ? ''
              : setStatus(expense_data[item.index].status, item.index)
          "
        >
          <p
            :class="
              expense_data[item.index].status || set_status
                ? 'text-[#12B76A] font-normal cursor-default '
                : 'text-[#2E90FA] font-normal'
            "
          >
            {{
              expense_data[item.index].status || set_status
                ? $t("expenses.auditCleared")
                : $t("expenses.auditClear")
            }}
          </p>
        </div>
      </template>

      <!-- Custom Menu Items -->
      <template #menu-item="{ item }">
        <v-menu location="start" v-if="!expense_data[item.index].status">
          <template v-slot:activator="{ props }">
            <v-icon
              icon="custom:dotVertical"
              v-bind="props"
              class="mt-2 mr-3 text-center cursor-pointer"
            ></v-icon>
          </template>
          <v-list class="rounded-lg items">
            <v-list-item
              @click="
                confirmDeleteExpense(item.index, expense_data[item.index])
              "
              class="my-n1 hover:bg-gray-200"
              >{{ $t("projectConfiguration.delete") }}</v-list-item
            >
            <v-list-item
              v-if="expense_data[item.index].file_title"
              @click="viewfile(item.index)"
              class="my-n1 hover:bg-gray-200"
              >{{ $t("cheques.viewFile") }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </app-custom-table>

    <div
      v-if="isExpenseUpdated"
      class="bg-amber-50 rounded-lg p-3 flex items-center gap-1 w-max"
    >
      <svg
        class="flex-shrink-0 w-4 h-4"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="#B45309"
        viewBox="0 0 20 20"
      >
        <path
          d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
        />
      </svg>
      <p class="text-sm text-amber-700">
        {{ $t("expenses.unsavedChanges") }}
      </p>
    </div>

    <div
      class="flex justify-end gap-4 fixed-header mt-7 p-3 rounded-lg"
      v-if="!shouldDisableInput"
    >
      <app-secondary-button
        :buttonLabel="$t('Cancel'.getTranslationKey())"
        @click-event="cancelAddNewExpense()"
      ></app-secondary-button>
      <app-primary-button
        @click-event="isEditExpense ? updateExpense() : saveExpense()"
        :buttonLabel="
          isEditExpense
            ? $t('Save Changes'.getTranslationKey())
            : $t('Save'.getTranslationKey())
        "
      >
      </app-primary-button>
    </div>
  </div>
  <app-dialog
    ref="dialog"
    :title="$t('expenses.deleteExpense')"
    :subtitle="$t('expenses.confirmDeleteExpense')"
    height="302"
    width="408"
  >
    <template #actions>
      <v-row class="px-4 mt-1 mb-5">
        <v-col cols="6" class="">
          <app-secondary-button
            :buttonLabel="$t('employees.cancel')"
            @click-event="hideDialog"
          >
          </app-secondary-button>
        </v-col>
        <v-col cols="6" class="">
          <v-btn
            variant="default"
            min-width="100%"
            min-height="100%"
            class="bg-[#D92D20] text-white !normal-case rounded-lg font-['Open_Sans'] text-[1rem] font-normal"
            @click="deleteExpense"
          >
            <!-- Yes , delete -->
            {{ $t("employees.yesDelete") }}
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </app-dialog>

  <additional-info
    ref="additionalInfo"
    :month="expenseMonthName"
    :year="String(expense_year)"
    currency=""
    @get-yearly-data="filterAllExpensesByMonthAndYear"
    @reset-action="() => {}"
  />

  <app-alert
    :title="$t(getTranslatedStringKey(alertTitle))"
    @close-alert="alertState = false"
    :state="alertState"
    :timeout="10000"
    :type="alertType"
    :multiple="multiple"
    :multipleTitles="multipleTitles"
  />
</template>

<script>
import {
  getTransString,
  maximumStartDate,
  minimumEndDate,
  restrictCharactersByRegex,
  currencyFormatter,
  monthNames,
  monthsNameWithNumber,
  getYearsFromProject,
} from "@/services/helpers";
import Api from "@/services/api";
import {
  addExpense,
  currencyBudgetPosition,
  updateExpense,
  uploadChequeFile,
  deleteExpense,
} from "@/services/endpoints";
import { mapState } from "vuex";
import ExpenseInfo from "./ExpenseInfo.vue";
import ExpenseBalanceOverview from "./ExpenseBalanceOverview.vue";
import { isEqual } from "lodash";
import OverviewHeading from "@/components/dashboard/projects/pm-local/projectOverview/OverviewHeading.vue";
import AdditionalInfo from "@/components/dashboard/projects/pm-local/projectOverview/AdditionalInfo.vue";
export default {
  name: "CreateEditExpense",
  components: {
    OverviewHeading,
    ExpenseInfo,
    ExpenseBalanceOverview,
    AdditionalInfo,
  },
  data() {
    return {
      overViewInfo: {
        name: "Overview",
      },
      currentDate: new Date(),
      selectedOption: "Cash",
      expense_data: [],
      original_expense_data: [],
      alertType: "success",
      alertState: false,
      alertTitle: "",
      multipleTitles: [],
      transformedCurrency: [],
      chequeBudgetPosition: [],
      transformedBudgetPosition: [],
      expense_year: null,
      expense_month: null,
      expense_type: "cash",
      set_status: false,
      saveClicked: false,
      floatingNumber: /^(?![0-9.-]+$).*$/g,
      serverErrors: "",
      expenseItem: "",
      expenseItemIndex: null,
      isExpenseUpdated: false,
      loading: true,
      projectYears: [],
      showErrorBanner: false,
      errorBannerOpen: true,
    };
  },
  watch: {
    selectedOption(val) {
      this.expense_data = [];
      this.getAllExpenses(false);
    },
    // isEditExpense() {
    //   this.expense_data = this.allExpenses?.expense_data;
    // this.expense_year =
    //   this.allExpenses?.duration?.year === ""
    //     ? this.currentDate.getFullYear()
    //     : this.allExpenses?.duration?.year;
    // this.expense_month =
    //   this.allExpenses?.duration?.month === ""
    //     ? this.getMonth()
    //     : this.allExpenses?.duration?.month;
    // },
    expense_data: {
      deep: true,
      handler(value) {
        this.isExpenseUpdated = !isEqual(value, this.original_expense_data);
      },
    },
  },
  computed: {
    tableHeaders() {
      return [
        { key: "no", label: "No." },
        { key: "title", label: "Title", type: "text" },
        { key: "document_date", label: "Doc. Date", type: "date" },
        { key: "payment_date", label: "Pay Date", type: "date" },
        { key: "supplier", label: "Supplier", type: "text" },
        { key: "currency_id", label: "Currency", type: "select" },
        {
          key: "total_amount_not_taxed",
          label: "Amt Excl. Tax",
          type: "currency",
        },
        { key: "total_amount_taxed", label: "Amt Incl. Tax", type: "currency" },
        { key: "budget_id", label: "Budget Position", type: "select" },
        { key: "file_title", label: "File", type: "file" },
        // { key: "status", label: "Audit Status", type: "status" },
      ];
    },
    appLoading() {
      return this.$store.getters["expenseModules/appLoading"];
    },
    canEditPmLocalModule() {
      return this.$store.state.auth.canEditModuleSections;
    },
    yearOptions() {
      return this.projectYears.map((year) => ({
        text: year?.toString(),
        value: year,
      }));
    },
    monthOptions() {
      return monthsNameWithNumber;
    },
    firstDayOfSelectedMonth() {
      const year = this.expense_year;
      const month = String(this.expense_month).padStart(2, "0");
      return `${year}-${month}-01`;
    },
    lastDayOfSelectedMonth() {
      const lastDay = new Date(
        this.expense_year,
        this.expense_month,
        0
      ).getDate();
      const month = String(this.expense_month).padStart(2, "0");
      return `${this.expense_year}-${month}-${String(lastDay).padStart(
        2,
        "0"
      )}`;
    },
    isForeignEstablishmentId() {
      return this.$store.getters["pmLocalModules/isForeignEstablishmentID"];
    },
    isProjectId() {
      return this.$store.getters["pmLocalModules/isProjectId"];
    },
    isEditExpense() {
      return this.$store.getters["expenseModules/isEditExpense"];
    },
    expenseMonthName() {
      return monthNames[this.expense_month - 1] ?? "";
    },
    isExpenseEditId() {
      return this.$store.getters["expenseModules/isExpenseEditId"];
    },

    isEditButton() {
      return this.$store.getters["hrResources/isEditButton"];
    },
    isEditPmLocal() {
      return this.$store.getters["hrResources/isEditEmployee"];
    },
    headers() {
      return this.$store.getters["expenseModules/isHeaders"];
    },
    allExpenses() {
      return this.$store.getters["expenseModules/isAllExpenses"];
    },
    isAllBalanceInfoData() {
      return this.$store.getters["projectOverview/isAllBalanceInfoData"];
    },
    allCurrencies() {
      const modifiedData = this.$store.getters["pmLocalModules/allCurrencies"];
      return modifiedData?.map((curr) => {
        return {
          text: curr.currency,
          value: curr.id,
        };
      });
    },
    balanceData() {
      const modifiedData = this.allExpenses.expense_overview;
      return modifiedData?.filter((item) => item.title === "Balance");
    },

    currentPage() {
      return this.$store.getters["expenseModules/isCurrentPage"];
    },
    currentPath() {
      return this.$route.path;
    },
    appLang() {
      return this.translation.locale;
    },
    ...mapState(["translation"]),
    shouldDisableInput() {
      // Add your condition to disable the input element here
      if (this.allExpenses?.duration?.is_locked) return true;
      return (
        this.currentPath === "/edit-pm-local" &&
        this.isEditPmLocal &&
        !this.isEditButton
      );
    },
  },
  methods: {
    openLockModal() {
      this.$refs.additionalInfo.openLockMonth("lock");
    },
    handleInputChange(value, field) {
      this.expense_data = [];
      if (field === "year") {
        this.expense_year = value;
      } else if (field === "month") {
        this.expense_month = value;
      }

      this.filterAllExpensesByMonthAndYear();
    },
    getPaymentMinDate(documentDate) {
      // Payment date must be within the selected month
      // AND cannot be before the document date
      if (!documentDate) {
        return this.firstDayOfSelectedMonth;
      }

      // Return the greater of document date or first day of selected month
      return documentDate > this.firstDayOfSelectedMonth
        ? documentDate
        : this.firstDayOfSelectedMonth;
    },
    handleMenuClick(item) {
      if (item.option === "add") {
        this.addNewExpenseRow();
      } else if (item.option === "Delete") {
        this.confirmDeleteExpense(item.index, item);
      } else if (item.option === "View File" && item.file_title) {
        this.viewfile(item.index);
      }
    },
    monthStringToNumber(monthString) {
      const date = new Date(`${monthString} 1, 2000`);
      const monthNumber = date.getMonth() + 1;

      if (!isNaN(monthNumber) && monthNumber >= 1 && monthNumber <= 12) {
        return monthNumber;
      } else {
        return -1; // This can be used to indicate an invalid month string
      }
    },
    getTranslatedStringKey(field) {
      return getTransString(field);
    },
    minEndDate(date) {
      return minimumEndDate(date);
    },
    maxStartDate(date) {
      return maximumStartDate(date);
    },
    viewfile(index) {
      window.open(this.expense_data[index].file_path, "_blank");
    },
    germanCurrency(curr) {
      return currencyFormatter(curr);
    },

    filterInput(event) {
      restrictCharactersByRegex(event, this.floatingNumber);
      // Define the regular expression for valid input
    },
    handleFileUpload(event, index) {
      const file = event.target.files[0];
      if (file) {
        const formData = new FormData();
        formData.append("cheque_file", file);
        Api()
          .post(`${uploadChequeFile}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            if (response.data.status === "success") {
              const fileData = response.data.data;
              const fileName =
                fileData.file_title.length > 10
                  ? fileData.file_title?.substring(0, 10) + "..."
                  : fileData.file_title;
              this.file_title = fileName;
              this.expense_data[index].file_title = fileData.file_title;
              this.expense_data[index].file_path = fileData.file_path;
              this.alertType = "success";
              this.alertState = true;
              this.alertTitle = response?.data?.details;
            }
          })
          .catch((error) => {
            this.alertType = "error";
            this.alertState = true;
            this.alertTitle = error?.response?.data?.details;
          });
      }
    },
    addNewExpenseRow() {
      const newIndex = this.expense_data.length + 1;
      this.expense_data.push({
        no: newIndex,
        title: "",
        document_date: "",
        payment_date: "",
        supplier: "",
        currency_id: null,
        status: this.set_status,
        total_amount_taxed: "",
        total_amount_not_taxed: "",
        budget_id: null,
        is_new: true,
      });
    },
    showDialog() {
      this.$refs.dialog.showDialog = true;
      this.subTitle = "pmLocals.confirmDeleteCurrency";
      this.title = "pmLocals.deleteCurrency";
      this.cancelLabel = "employees.noKeepIt";
    },

    hideDialog() {
      this.$refs.dialog.showDialog = false;
    },
    confirmDeleteExpense(index, expense) {
      if (expense.id) {
        this.$refs.dialog.showDialog = true;
        this.cancelLabel = "employees.noKeepIt";
        this.expenseItem = expense;
        this.expenseItemIndex = index;
      } else {
        this.deleteRow(index);
      }
    },
    deleteRow(index) {
      this.expense_data.splice(index, 1);
      this.expense_data = this.expense_data.map((item, i) => ({
        ...item,
        no: i + 1,
      }));
      this.hideDialog();
    },

    async deleteExpense() {
      try {
        const response = await Api().delete(
          `${deleteExpense}${this.expenseItem.id}/`
        );
        if (response.data.status === "success") {
          this.hideDialog();
          this.alertType = "success";
          this.alertState = true;
          this.alertTitle = response?.data?.detail;
          await this.getAllExpenses(false);
        }
      } catch (error) {
        this.alertType = "error";
        this.alertState = true;
        this.alertTitle = error?.response?.data?.detail;
      }
    },
    setStatus(data, index) {
      if (data === false) {
        this.expense_data[index].status = !data;
      }
    },

    navigateToExpensePdf() {
      this.$store.commit(
        "expenseModules/SET_PDF_MONTH_TO_STATE",
        this.expense_month
      );
      this.$store.commit(
        "expenseModules/SET_PDF_YEAR_TO_STATE",
        this.expense_year
      );
      this.$store.commit(
        "expenseModules/SET_PDF_EXPENSE_TYPE_TO_STATE",
        this.selectedOption?.toLocaleLowerCase()
      );
      this.$router.push({ name: "viewExpensePdf" });
    },

    cancelAddNewExpense() {
      this.expense_data = [];
      this.getAllExpenses(false);
    },
    getBudgetPosition() {
      Api()
        .get(`${currencyBudgetPosition}2/${this.isForeignEstablishmentId}`)
        .then((response) => {
          if (response.data.status === "success") {
            this.chequeBudgetPosition = response.data.data?.map((pos) => {
              return {
                text: pos.budget_item__position,
                value: pos.id,
              };
            });
          }
        })
        .catch();
    },
    saveExpense() {
      this.validateFields();
      if (this.hasErrors) return;
      this.expense_data.forEach((expense) => {
        if (expense.total_amount_not_taxed === "") {
          delete expense.total_amount_not_taxed;
        }
      });
      const payload = {
        expense_data: this.expense_data.map(({ no, is_new, ...rest }) => rest),
        month: +this.expense_month,
        year: +this.expense_year,
        expense_type: this.selectedOption?.toLocaleLowerCase(),
        foreign_establishment_id: this.isForeignEstablishmentId,
        project_id: this.isProjectId,
      };
      Api()
        .post(`${addExpense}`, payload)
        .then((response) => {
          if (response.data.status === "success") {
            this.alertType = "success";
            this.alertState = true;
            this.multiple = false;
            this.alertTitle = response?.data?.detail;
            this.expense_data = payload.expense_data;
            this.getAllExpenses(false);
            this.hasErrors = false;
            this.saveClicked = false;
            this.showErrorBanner = false;
          }
        })
        .catch((error) => {
          if (error?.response?.status === 314) {
            this.serverErrors = error?.response?.data?.data;
            this.alertType = "error";
            this.alertState = true;
            this.alertTitle =
              "Some expense entries contain errors. Please refer to the details below.";
            // this.multiple = true;
            this.hasErrors = true;
            this.showErrorBanner = true;
            this.errorBannerOpen = true;

            this.multipleTitles = Object.entries(this.serverErrors).reduce(
              (acc, [number, item]) => {
                Object.keys(item).forEach((key) => {
                  if (key === "currency") {
                    acc.push({
                      key: item[key],
                      message: "Insufficient currency funds for",
                      amount: item?.currency_amount,
                      title: item?.expense_title,
                      pay_date: item?.date,
                      number: Number(number) + 1,
                    });
                  } else if (key === "budget_position") {
                    acc.push({
                      key: item[key],
                      message: "Insufficient local budget funds for",
                      amount: item?.budget_position_amount,
                      title: item?.expense_title,
                      pay_date: item?.date,
                      number: Number(number) + 1,
                    });
                  }
                });
                return acc;
              },
              []
            );
          } else {
            this.alertType = "error";
            this.alertState = true;
            this.alertTitle = error?.response?.data?.detail;
            this.hasErrors = false;
            this.saveClicked = false;
          }
        });
    },
    updateExpense() {
      this.validateFields();
      if (this.hasErrors) return;
      this.expense_data.forEach((expense) => {
        if (expense.total_amount_not_taxed === "") {
          delete expense.total_amount_not_taxed;
        }
      });
      const payload = {
        expense_data: this.expense_data.map(({ no, is_new, ...rest }) => rest),
        month: this.monthStringToNumber(this.expense_month),
        year: +this.expense_year,
        expense_type: this.selectedOption?.toLocaleLowerCase(),
        foreign_establishment_id: this.isForeignEstablishmentId,
        project_id: this.isProjectId,
      };
      Api()
        .put(`${updateExpense}`, payload)
        .then((response) => {
          if (response.data.status === "success") {
            this.alertType = "success";
            this.alertState = true;
            this.multiple = false;
            this.alertTitle = response?.data?.detail;
            this.expense_data = payload.expense_data;
            this.getAllExpenses(false);
            this.hasErrors = false;
            this.saveClicked = false;
            this.showErrorBanner = false;
          }
        })
        .catch((error) => {
          if (error?.response?.status === 314) {
            this.serverErrors = error?.response?.data?.data;
            this.alertType = "error";
            this.alertState = true;
            this.alertTitle =
              "Some expense entries contain errors. Please refer to the details below.";
            // this.multiple = true;
            this.hasErrors = true;
            this.showErrorBanner = true;
            this.errorBannerOpen = true;

            this.multipleTitles = Object.entries(this.serverErrors).reduce(
              (acc, [number, item]) => {
                Object.keys(item).forEach((key) => {
                  if (key === "currency") {
                    acc.push({
                      key: item[key],
                      message: "Insufficient currency funds for",
                      amount: item.currency_amount,
                      title: item?.expense_title,
                      pay_date: item?.date,
                      number: Number(number) + 1,
                    });
                  } else if (key === "budget_position") {
                    acc.push({
                      key: item[key],
                      message: "Insufficient local budget funds for",
                      amount: item.budget_position_amount,
                      title: item?.expense_title,
                      pay_date: item?.date,
                      number: Number(number) + 1,
                    });
                  }
                });
                return acc;
              },
              []
            );
          } else {
            this.alertType = "error";
            this.alertState = true;
            this.alertTitle = error?.response?.data?.detail;
            this.hasErrors = false;
            this.saveClicked = false;
          }
        });
    },
    async getAllExpenses(data) {
      if (data) {
        this.loading = true;
      }
      try {
        const payload = {
          load_page: false,
          expense_type: this.selectedOption?.toLocaleLowerCase(),
          foreign_establishment_id: this.isForeignEstablishmentId,
          month: +this.expense_month,
          year: +this.expense_year,
        };

        await this.$store.dispatch("expenseModules/getAllExpenses", payload);
        this.generateRows();
      } catch (e) {
        console.error("error:::", e);
      } finally {
        this.loading = false;
      }
    },
    async filterAllExpensesByMonthAndYear() {
      try {
        const payload = {
          load_page: false,
          expense_type: this.selectedOption?.toLocaleLowerCase(),
          foreign_establishment_id: this.isForeignEstablishmentId,
          month: +this.expense_month,
          year: +this.expense_year,
        };

        await this.$store.dispatch(
          "expenseModules/filterAllExpensesByMonthAndYear",
          payload
        );

        this.generateRows();
      } catch (e) {
        console.error("Error:::", e);
      }
    },
    getExpenseTotals() {
      const initialValue = { total_amount_taxed: 0, total_amount_not_taxed: 0 };
      const sum = this.expense_data?.reduce((accumulator, item) => {
        accumulator.total_amount_taxed += Number(item.total_amount_taxed);
        accumulator.total_amount_not_taxed += Number(
          item.total_amount_not_taxed
        );
        return accumulator;
      }, initialValue);
      return sum;
    },
    generateRows() {
      const unsavedItems = this.expense_data.filter((item) => item.is_new);
      this.expense_data = [];
      this.original_expense_data = [];
      if (this.isEditExpense) {
        const expenses = [
          ...(this.allExpenses?.expense_data || []),
          ...unsavedItems,
        ].map((item, index) => ({ ...item, no: index + 1 }));
        this.expense_data = expenses;
        this.original_expense_data = structuredClone(expenses);
      } else {
        const newIndex = this.expense_data.length + 1;
        this.original_expense_data.push({
          no: newIndex,
          title: "",
          document_date: "",
          payment_date: "",
          supplier: "",
          currency_id: null,
          status: this.set_status,
          total_amount_taxed: "",
          total_amount_not_taxed: "",
          budget_id: null,
          is_new: true,
        });
        this.addNewExpenseRow();
      }
    },
    capitalizeFirstLetter(str) {
      return str?.charAt(0)?.toUpperCase() + str?.slice(1);
    },
    getMonth() {
      const currentDate = new Date();
      return new Intl.DateTimeFormat("en-US", { month: "long" })?.format(
        currentDate
      );
    },
    // getCurrencyName(currency_id, index){
    // },
    checkAlertEror(message) {
      this.alertState = true;
      this.alertType = "error";
      this.alertTitle = message;
    },
    rowErrorClass(expense, index) {
      if (!this.saveClicked) {
        return ""; // Do not validate if the save button hasn't been clicked
      }
      if (
        !expense.title ||
        !expense.document_date ||
        !expense.payment_date ||
        !expense.supplier ||
        !expense.currency_id ||
        !expense.total_amount_taxed ||
        !expense.budget_id ||
        expense.total_amount_not_taxed > expense.total_amount_taxed
        // Add checks for other fields if necessary
      ) {
        return this.hasErrors ? "border-red-300 border-2" : "border-2";
      }
      if (this.serverErrors && this.serverErrors[index]) {
        return this.hasErrors ? "border-red-300 border-2" : "border-2";
      }
      return "";
    },

    validateFields() {
      this.hasErrors = false;
      this.saveClicked = true;
      const fieldsConfig = {
        budget_id: "Budget position is required",
        total_amount_taxed: "Total amount taxed is required",

        currency_id: "Currency is required",
        supplier: "Supplier is required",
        payment_date: "Payment date is required",
        document_date: "Document date is required",
        title: "Title is required",
        // Add other fields as needed...
      };
      // const orderedFields = ['title', 'document_date', 'payment_date', 'supplier', 'currency_id', 'total_amount_taxed', 'total_amount_not_taxed'];
      this.expense_data?.forEach((expense) => {
        for (const [field, errorMessage] of Object.entries(fieldsConfig)) {
          if (!expense[field]) {
            this.hasErrors = true;
            this.checkAlertEror(errorMessage);
          }
          if (
            Number(expense["total_amount_not_taxed"]) >
            Number(expense["total_amount_taxed"])
          ) {
            this.hasErrors = true;
            this.checkAlertEror(
              "Amt Excl. Tax cannot be greater than Amt Incl. Tax"
            );
          }
        }
      });
      setTimeout(() => {
        this.hasErrors = false;
        this.saveClicked = false;
      }, 1500);
    },
    async fetchProjectYears() {
      try {
        const response = await Api().get(
          `/projects/project/pm-local/get-project-years/${this.isForeignEstablishmentId}`
        );

        if (response.data.status === "success") {
          this.projectYears = response.data.data || [];

          // Get current year as string
          const currentYear = this.currentDate.getFullYear();

          // Check if current year is in the list
          if (this.projectYears.includes(currentYear)) {
            this.expense_year = currentYear;
          } else if (this.projectYears.length > 0) {
            // Set to the last year in the list
            const lastYear = this.projectYears[this.projectYears.length - 1];
            this.expense_year = lastYear;
          }

          // Set month to current month (1-12)
          this.expense_month = this.currentDate.getMonth() + 1;
        }
      } catch (error) {
        this.alertType = "error";
        this.alertState = true;
        this.alertTitle =
          error?.response?.data?.detail || "Failed to fetch project years";
        console.error("Error fetching project years:", error);
      }
    },
    async getResources() {
      try {
        await this.fetchProjectYears();
        await this.$store.dispatch("pmLocalModules/getAllCurrencies");
        await this.getAllExpenses(true);
      } catch (e) {
        console.error("error:::", e);
      }
    },
  },
  mounted() {
    this.getResources();
  },
  created() {
    this.$store.commit("auth/SET_RAIL_TO_STATE", true);
    this.getBudgetPosition();
    // this.generateRows();
    // this.expense_year =
    //   this.allExpenses?.duration?.year || this.currentDate.getFullYear();
    // this.expense_month =
    //   this.allExpenses?.duration?.month || this.currentDate.getMonth() + 1;
    // this.selectedOption =
    //   this.capitalizeFirstLetter(
    //     this.allExpenses?.expense_data[0]?.expense_type
    //   ) || "Cash";
  },
};
</script>

<style scoped>
table {
  border-collapse: collapse;
  width: 100%;
}

th,
td {
  padding: 8px 8px;
  text-align: left;
}

tbody tr:nth-child(even) {
  background-color: #f9fafb;
}
tr > th {
  background-color: #f2f4f7;
  border-right: 1px solid #eaecf0;
}

tr > td {
  border-right: 1px solid #eaecf0;
}

th:nth-last-child(1),
td:nth-last-child(1) {
  border-right: none;
}

tr {
  border-bottom: 1px solid #eaecf0;
}

tr:nth-last-child(1) {
  border-bottom: none;
}

input {
  width: 100%;
  box-sizing: border-box;
  padding: 6px;
  outline: none;
}
input:focus {
  background: #ffffff;
  border: 1px solid #fdc6c2;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05),
    0 0 0 4px rgba(139, 41, 35, 0.04);
  border-radius: 8px;
}

.no-padding {
  padding: 0px !important;
}

::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

::-webkit-scrollbar-thumb:hover {
  cursor: pointer;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
